import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Users,
  Target,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  Bot,
  Trophy,
  BarChart2,
} from 'lucide-react';
import { getEuroLeagueTeamMetadata } from '../../utils/basketballDataService';
import { C } from '../../components/design-system/primitives/constants';

/**
 * BasketballDashboard - Minimal, coach-focused dashboard
 *
 * Displays Real Madrid team data with essential metrics:
 * - Team records (EuroLeague + Liga ACB)
 * - Key performance stats
 * - Quick actions for coaches
 */
export default function BasketballDashboard() {
  const navigate = useNavigate();

  // Get Real Madrid team metadata
  const teamData = getEuroLeagueTeamMetadata('Real Madrid');

  // Stats cards using real team data
  const statsCards = useMemo(() => {
    if (!teamData) {
      return [
        { id: 'record', label: 'EuroLeague', value: '--', subtext: 'Loading...', icon: <Trophy size={20} />, color: 'orange' },
        { id: 'ppg', label: 'PPG', value: '--', subtext: 'Loading...', icon: <Activity size={20} />, color: 'green' },
        { id: 'netRating', label: 'Net Rating', value: '--', subtext: 'Loading...', icon: <TrendingUp size={20} />, color: 'blue' },
        { id: 'fgPct', label: 'FG%', value: '--', subtext: 'Loading...', icon: <Target size={20} />, color: 'purple' },
      ];
    }

    const { euroLeagueRecord, ligaACBRecord, ppg, oppPpg, netRating, fgPct, threePct, streakType, streakCount } = teamData;

    return [
      {
        id: 'euroLeague',
        label: 'EuroLeague',
        value: `${euroLeagueRecord.wins}-${euroLeagueRecord.losses}`,
        subtext: `Position: ${euroLeagueRecord.position}th`,
        icon: <Trophy size={20} />,
        color: 'orange',
        trend: `${streakType}${streakCount}`,
        trendUp: streakType === 'W',
      },
      {
        id: 'ligaACB',
        label: 'Liga ACB',
        value: `${ligaACBRecord.wins}-${ligaACBRecord.losses}`,
        subtext: `Position: ${ligaACBRecord.position}st`,
        icon: <Trophy size={20} />,
        color: 'blue',
        trend: 'League Leader',
        trendUp: true,
      },
      {
        id: 'ppg',
        label: 'Points Per Game',
        value: ppg.toFixed(1),
        subtext: `Opp: ${oppPpg.toFixed(1)} PPG`,
        icon: <Activity size={20} />,
        color: 'green',
        trend: `+${(ppg - oppPpg).toFixed(1)} diff`,
        trendUp: ppg > oppPpg,
      },
      {
        id: 'netRating',
        label: 'Net Rating',
        value: `+${netRating.toFixed(1)}`,
        subtext: `FG: ${(fgPct * 100).toFixed(1)}% | 3PT: ${(threePct * 100).toFixed(1)}%`,
        icon: <BarChart2 size={20} />,
        color: 'purple',
        trend: 'Top 10',
        trendUp: netRating > 0,
      },
    ];
  }, [teamData]);

  // Quick actions - minimal set for coaches
  const quickActions = useMemo(() => [
    {
      id: 'askAI',
      title: 'Ask SLAM AI',
      description: 'Get instant basketball insights',
      icon: <MessageSquare size={24} />,
      path: '/basketball/ask-slam-ai',
      highlight: true,
    },
    {
      id: 'roster',
      title: 'View Roster',
      description: 'Real Madrid player stats',
      icon: <Users size={24} />,
      path: '/basketball/roster-management',
    },
    {
      id: 'agents',
      title: 'Agent Builder',
      description: 'Build AI analytics agents',
      icon: <Bot size={24} />,
      path: '/basketball/canvas',
    },
    {
      id: 'biv2',
      title: 'BIv2 Shot Matrix',
      description: 'Moneyball shot intelligence',
      icon: <Target size={24} />,
      path: '/basketball/bi-v2',
    },
  ], []);

  // Earned OLED colors — mapped to C constants
  const colorClasses = {
    orange: { bg: `${C.gold}20`, text: C.gold },
    green: { bg: `${C.elite}20`, text: C.elite },
    blue: { bg: `${C.gen}20`, text: C.gen },
    purple: { bg: '#9333ea20', text: '#a855f7' }, // no C.purple token — one-off accent
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header - Minimal */}
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 200, letterSpacing: -0.5, color: C.text }}>
            {teamData?.name || 'Real Madrid Baloncesto'}
          </h1>
          <p style={{ fontSize: 13, color: C.t3 }}>
            Coach: {teamData?.coach || 'Sergio Scariolo'} • {teamData?.season || '2025-26'}
          </p>
        </div>
        {/* Team Logo Placeholder */}
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white"
          style={{ backgroundColor: '#0052AD' }} // Real Madrid brand blue
        >
          RM
        </div>
      </div>

      {/* Stats Grid - 4 Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((card) => {
          const colors = colorClasses[card.color];
          return (
            <div
              key={card.id}
              className="p-4 rounded-[14px]"
              style={{ background: C.s1, border: `1px solid ${C.border}` }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium uppercase tracking-wider" style={{ color: C.t3 }}>
                  {card.label}
                </span>
                <div className="p-1.5 rounded-lg" style={{ background: colors.bg }}>
                  <span style={{ color: colors.text }}>{card.icon}</span>
                </div>
              </div>
              <p style={{ fontSize: 24, fontWeight: 200, color: C.text }}>
                {card.value}
              </p>
              <p className="text-xs" style={{ color: C.t3 }}>
                {card.subtext}
              </p>
              {card.trend && (
                <div className="mt-2 flex items-center gap-1">
                  {card.trendUp ? (
                    <TrendingUp size={12} style={{ color: C.elite }} />
                  ) : (
                    <TrendingDown size={12} style={{ color: C.dev }} />
                  )}
                  <span className="text-xs" style={{ color: card.trendUp ? C.elite : C.dev }}>
                    {card.trend}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Actions - Minimal Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <button
            key={action.id}
            onClick={() => navigate(action.path)}
            className="p-4 rounded-[14px] text-left transition-all hover:scale-[1.02]"
            style={{
              background: action.highlight ? C.g08 : C.s1,
              border: `1px solid ${action.highlight ? C.goldDim : C.border}`,
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="p-2 rounded-lg"
                style={{
                  background: action.highlight ? C.gold : C.s3,
                  color: action.highlight ? '#000' : C.t2,
                }}
              >
                {action.icon}
              </div>
              <div>
                <h3 className="font-semibold text-sm" style={{ color: C.text }}>
                  {action.title}
                </h3>
                <p className="text-xs" style={{ color: C.t3 }}>
                  {action.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
